import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import { useAudioScrubber } from "../../hooks/useAudioScrubber";
import GenerateButton from "../GenerateButton/GenerateButton";
import { getDisplayTitle } from "../Setlist/trackUtils";
import {
  getStoredGenerateParams,
  buildGenerateQueryString,
} from "../../utils/generationParams";
import "./Navigation.css";

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasSetlist, setHasSetlist] = useState(false);
  const {
    currentTrack,
    isPlaying,
    audioRef,
    nextTrack,
    previousTrack,
    tracks,
  } = useAudioPlayer();

  const {
    displayTime,
    duration,
    progressPercent,
    progressBarRef,
    handleMouseDown,
    handleSeek,
    formatTime,
  } = useAudioScrubber(audioRef, currentTrack);

  useEffect(() => {
    const checkSetlist = () => {
      setHasSetlist(!!localStorage.getItem("lastSetlist"));
    };

    checkSetlist();
    window.addEventListener("storage", checkSetlist);

    return () => window.removeEventListener("storage", checkSetlist);
  }, [location]);

  const togglePlayPause = () => {
    if (audioRef?.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch((err) => {
          // AbortError fires if play is interrupted by pause; ignore it to avoid noisy console errors
          if (err?.name !== "AbortError") {
            console.error("[Navigation] Playback failed:", err);
          }
        });
      }
    }
  };

  const handleGenerateClick = () => {
    const storedParams = getStoredGenerateParams();
    const queryString = buildGenerateQueryString(storedParams || {});
    navigate(`/generate${queryString ? `?${queryString}` : ""}`);
  };

  if (location.pathname === "/generate") {
    return null;
  }

  const trackDisplayTitle = getDisplayTitle(currentTrack);

  return (
    <nav className="main-nav">
      <div className="nav-content">
        <div className="nav-left">
          <NavLink to="/" className="nav-brand">
            🎸 Inphinite
          </NavLink>

          <div className="nav-links">
            <NavLink
              to="/"
              className={({ isActive }) =>
                isActive ? "nav-link active" : "nav-link"
              }
            >
              Home
            </NavLink>

            {hasSetlist && (
              <NavLink
                to="/setlist"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Setlist
              </NavLink>
            )}

            <NavLink
              to="/features"
              className={({ isActive }) =>
                isActive ? "nav-link active" : "nav-link"
              }
            >
              Features
            </NavLink>
          </div>
        </div>

        <div className="nav-right">
          {currentTrack && (
            <div className="mini-player">
              <button
                className="mini-control-btn"
                onClick={previousTrack}
                disabled={!tracks || tracks.length === 0}
                aria-label="Previous track"
                title="Previous"
              >
                ⏮
              </button>

              <button
                className="play-pause-btn"
                onClick={togglePlayPause}
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? "⏸" : "▶"}
              </button>

              <button
                className="mini-control-btn"
                onClick={nextTrack}
                disabled={!tracks || tracks.length === 0}
                aria-label="Next track"
                title="Next"
              >
                ⏭
              </button>

              <div className="track-info">
                <div className="track-name">{trackDisplayTitle}</div>
                <div className="track-meta">
                  {currentTrack.date} • {formatTime(displayTime)} /{" "}
                  {formatTime(duration)}
                </div>
                <div
                  ref={progressBarRef}
                  className="progress-bar"
                  onClick={handleSeek}
                  onMouseDown={handleMouseDown}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className="progress-fill"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          <GenerateButton hasSetlist={hasSetlist} onClick={handleGenerateClick} />
        </div>
      </div>
    </nav>
  );
}

export default Navigation;
